import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ApiError, api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { PlatformLogo } from "@/components/PlatformLogo";
import { usePublishProgress } from "@/components/PublishProgress";
import type { PipelineEvent, StoredCredential, TeamMember } from "@/types";

const BASE_STEPS = [
  { id: "scrape", label: "Scrape source listing" },
  { id: "auth", label: "Authenticate with MyHome" },
  { id: "upload", label: "Upload images" },
  { id: "create", label: "Create listing" },
];
const SS_STEPS = [
  { id: "ss_auth", label: "Authenticate with SS.ge" },
  { id: "ss_upload", label: "Upload images (SS.ge)" },
  { id: "ss_create", label: "Create listing (SS.ge)" },
];

type StepState = "pending" | "active" | "done" | "error";
interface Step {
  id: string;
  label: string;
  state: StepState;
  detail: string;
}
type View = "idle" | "running" | "done" | "failed";

export function Publish() {
  const [creds, setCreds] = useState<StoredCredential[]>([]);
  const [loadingCreds, setLoadingCreds] = useState(true);
  const [geminiConfigured, setGeminiConfigured] = useState(false);

  const { user } = useAuth();
  // Manager-and-above may publish a listing on behalf of an agent they
  // manage — same idea as the bulk flow, one listing at a time.
  const canPublishAsOthers =
    user?.role === "executive_manager" ||
    user?.role === "operations_manager" ||
    user?.role === "lead_broker";
  const [team, setTeam] = useState<TeamMember[]>([]);
  // "" = publish under the caller's own account.
  const [publishAsId, setPublishAsId] = useState<string>("");

  const [params] = useSearchParams();
  const [url, setUrl] = useState(params.get("url") ?? "");
  const [description, setDescription] = useState("");
  const [mhPayBalance, setMhPayBalance] = useState(true);
  // Default the "Also publish to SS.ge" toggle to ON after creds load, but only
  // when the user has SS.ge credentials saved — otherwise the chip is disabled
  // anyway and we don't want to mislead.
  const [useSs, setUseSs] = useState(true);
  const [customPriceEnabled, setCustomPriceEnabled] = useState(false);
  const [customPriceUsd, setCustomPriceUsd] = useState<string>("");
  const [ownerPhone, setOwnerPhone] = useState<string>("");

  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const [logs, setLogs] = useState<string[]>([]);
  const [result, setResult] = useState<any>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  const [geminiBusy, setGeminiBusy] = useState(false);
  const [geminiPrompt, setGeminiPrompt] = useState("");
  const [aiToolsOpen, setAiToolsOpen] = useState(false);
  // Durable JobRun id of the last publish — needed to re-run a single
  // platform via /api/job-runs/{id}/retry-platform when SS.ge failed
  // but MyHome succeeded.
  const [jobRunId, setJobRunId] = useState<string | null>(null);
  const [retrySsBusy, setRetrySsBusy] = useState(false);
  const [retrySsError, setRetrySsError] = useState<string | null>(null);
  const [retrySsStarted, setRetrySsStarted] = useState(false);

  const { register } = usePublishProgress();

  const hasMh = !!creds.find((c) => c.platform === "myhome");
  const hasSs = !!creds.find((c) => c.platform === "ss");

  // Agents the caller may publish on behalf of — active teammates other
  // than the caller. ``/api/team`` already scopes this to who the caller
  // manages (a manager sees only their own direct reports).
  const publishableAgents = useMemo(
    () => team.filter((m) => m.id !== user?.id),
    [team, user?.id],
  );
  const publishAs = useMemo(
    () => publishableAgents.find((m) => m.id === publishAsId) ?? null,
    [publishableAgents, publishAsId],
  );
  // Credentials belong to whoever the listing is published as: the
  // caller's own vault for a self-publish, the picked agent's saved
  // creds flags when publishing on someone's behalf.
  const effectiveHasMh = publishAs
    ? publishAs.has_myhome_creds && publishAs.has_myhome_phone
    : hasMh;
  const effectiveHasSs = publishAs ? publishAs.has_ss_creds : hasSs;

  useEffect(() => {
    (async () => {
      const [c, g] = await Promise.all([
        api<StoredCredential[]>("/api/credentials"),
        api<{ configured: boolean }>("/api/gemini-configured"),
      ]);
      setCreds(c);
      setGeminiConfigured(g.configured);
      setLoadingCreds(false);
    })();
  }, []);

  // Load the team roster so a manager+ can pick a publish-as target.
  // Silently degrades to self-only if the caller lacks team access.
  useEffect(() => {
    if (!canPublishAsOthers) return;
    let cancelled = false;
    (async () => {
      try {
        const t = await api<TeamMember[]>("/api/team");
        if (!cancelled) setTeam(t.filter((m) => m.status === "active"));
      } catch {
        /* no team access → picker stays hidden */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canPublishAsOthers]);

  // Sync the "Also publish to SS.ge" toggle with whether SS creds exist.
  // Wait for the creds load to finish so the initial `true` default isn't wiped
  // out by an empty creds list during the first ~100ms.
  useEffect(() => {
    if (loadingCreds) return;
    setUseSs(effectiveHasSs);
  }, [loadingCreds, effectiveHasSs]);

  const logPanelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = logPanelRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [logs.length]);

  const canSubmit = useMemo(() => {
    if (!url.trim() || !description.trim() || !effectiveHasMh) return false;
    if (customPriceEnabled) {
      const n = Number(customPriceUsd);
      if (!Number.isFinite(n) || n <= 0) return false;
    }
    return true;
  }, [url, description, effectiveHasMh, customPriceEnabled, customPriceUsd]);

  const view: View = useMemo(() => {
    if (running) return "running";
    if (fatal) return "failed";
    if (result) return "done";
    return "idle";
  }, [running, fatal, result]);

  function buildInitialSteps(): Step[] {
    const mk = (s: { id: string; label: string }): Step => ({
      id: s.id,
      label: s.label,
      state: "pending",
      detail: "",
    });
    const out: Step[] = BASE_STEPS.map(mk);
    if (mhPayBalance) {
      out.push(mk({ id: "pay", label: "Activate with MyHome balance" }));
    }
    if (useSs) {
      out.push(...SS_STEPS.map(mk));
    }
    return out;
  }

  async function polish(mode: "fix" | "custom") {
    if (geminiBusy) return;
    if (mode === "custom" && !geminiPrompt.trim()) return;
    if (mode === "fix" && !description.trim()) return;
    setGeminiBusy(true);
    try {
      const data = await api<{ result: string }>("/api/gemini", {
        method: "POST",
        json: { text: description, mode, prompt: geminiPrompt, context: url },
      });
      setDescription(data.result);
      if (mode === "custom") setGeminiPrompt("");
    } catch (e) {
      alert(e instanceof Error ? e.message : "Gemini request failed");
    } finally {
      setGeminiBusy(false);
    }
  }

  async function generateFromUrl() {
    if (geminiBusy) return;
    if (!url.trim()) {
      alert("Paste the source listing URL above first.");
      return;
    }
    setGeminiBusy(true);
    try {
      const data = await api<{ description: string; source: string }>(
        "/api/gemini/generate-from-url",
        { method: "POST", json: { url: url.trim() } },
      );
      setDescription(data.description);
    } catch (e) {
      // The most useful failure surface here is "listing_not_in_catalog"
      // — the source URL wasn't ingested yet, so we have no structured
      // params to feed Gemini. Show a friendlier message than the raw
      // backend code.
      const msg =
        e instanceof ApiError && e.message.includes("listing_not_in_catalog")
          ? "We haven't ingested this listing yet. Try again in a minute, or polish a manual description with ✨ Polish."
          : e instanceof Error
            ? e.message
            : "Generation failed";
      alert(msg);
    } finally {
      setGeminiBusy(false);
    }
  }

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (running || !canSubmit) return;
    setError(null);
    setFatal(null);
    setResult(null);
    setLogs([]);
    setSteps(buildInitialSteps());
    setRunning(true);

    try {
      const body: Record<string, unknown> = {
        url: url.trim(),
        description: description.trim(),
        mh_pay_balance: mhPayBalance,
        use_ss: useSs,
      };
      if (customPriceEnabled) {
        body.custom_price_enabled = true;
        body.custom_price_usd = Number(customPriceUsd);
      }
      if (ownerPhone.trim()) {
        body.owner_phone = ownerPhone.trim();
      }
      if (publishAsId) {
        body.publish_as_user_id = publishAsId;
      }
      const { job_id, job_run_id } = await api<{
        job_id: string;
        job_run_id: string;
      }>("/api/run", {
        method: "POST",
        json: body,
      });
      setJobRunId(job_run_id);

      const es = new EventSource(`/api/stream/${job_id}`, { withCredentials: true });
      es.onmessage = (ev) => {
        const evt: PipelineEvent = JSON.parse(ev.data);
        if (evt.step === "__close__") {
          es.close();
          setRunning(false);
          return;
        }
        if (evt.message) {
          setLogs((ls) => [...ls, evt.message!]);
        }
        if (evt.step === "finished") {
          if (evt.status === "done" && evt.result) setResult(evt.result);
          if (evt.status === "error") {
            setFatal(evt.message || "Pipeline failed");
            setSteps((ss) =>
              ss.map((s) => (s.state === "active" ? { ...s, state: "error" } : s)),
            );
          }
          return;
        }
        setSteps((ss) =>
          ss.map((s) => {
            if (s.id !== evt.step) return s;
            if (evt.status === "start") return { ...s, state: "active", detail: "" };
            if (evt.status === "done")
              return { ...s, state: "done", detail: evt.message || "" };
            if (evt.status === "error")
              return { ...s, state: "error", detail: evt.message || "" };
            if (evt.status === "progress")
              return { ...s, detail: evt.message || s.detail };
            return s;
          }),
        );
      };
      es.onerror = () => {
        es.close();
        setRunning((r) => {
          if (r) setFatal("Connection to server lost.");
          return false;
        });
      };
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : "Request failed");
      }
      setRunning(false);
    }
  }

  /** Re-publish ONLY SS.ge after a partial failure (MyHome landed, SS
   *  errored). Routes through ``retry-platform`` so the MyHome listing is
   *  never re-created. Progress is handed to the floating publish panel —
   *  it already renders the per-platform retry cleanly. */
  async function retrySs() {
    if (retrySsBusy || !jobRunId) return;
    setRetrySsBusy(true);
    setRetrySsError(null);
    try {
      const r = await api<{ job_id: string; job_run_id: string }>(
        `/api/job-runs/${jobRunId}/retry-platform`,
        {
          method: "POST",
          json: { platform: "ss", description: description.trim() },
        },
      );
      register({
        jobId: r.job_id,
        jobRunId: r.job_run_id,
        origin: "single",
        title: null,
        sourceUrl: url,
        agentLabel: publishAs
          ? publishAs.full_name || publishAs.email
          : null,
        agentAvatarUrl: publishAs?.avatar_url ?? null,
        useSs: true,
        onlyPlatform: "ss",
      });
      setRetrySsStarted(true);
    } catch (e) {
      let msg = e instanceof Error ? e.message : "Retry failed";
      if (e instanceof ApiError) {
        const body = e.body as
          | { detail?: { message?: string } | string }
          | null
          | undefined;
        if (body && typeof body === "object" && body.detail) {
          if (typeof body.detail === "object" && body.detail.message) {
            msg = body.detail.message;
          } else if (typeof body.detail === "string") {
            msg = body.detail;
          }
        }
      }
      setRetrySsError(msg);
    } finally {
      setRetrySsBusy(false);
    }
  }

  function resetToIdle() {
    setResult(null);
    setFatal(null);
    setError(null);
    setLogs([]);
    setSteps([]);
    setUrl("");
    setDescription("");
    setJobRunId(null);
    setRetrySsStarted(false);
    setRetrySsError(null);
  }

  if (loadingCreds) {
    return <div className="text-xs text-slate-600">loading…</div>;
  }

  const progressDone = steps.filter((s) => s.state === "done").length;
  const progressTotal = steps.length || 1;
  const pct = Math.round((progressDone / progressTotal) * 100);

  return (
    <div className="w-full">
      <PageHeader
        title={
          view === "running"
            ? "Publishing…"
            : view === "done"
              ? "Listing published"
              : view === "failed"
                ? "Publish failed"
                : "Publish a listing"
        }
        subtitle={
          view === "idle"
            ? "Paste a MyHome.ge or home.ss.ge listing URL. We scrape it, re-upload the photos, and post it under your account."
            : undefined
        }
        actions={
          view === "idle" ? (
            <button
              type="submit"
              form="publish-form"
              disabled={running || !canSubmit}
              className="btn-primary"
            >
              Publish listing
            </button>
          ) : view === "done" || view === "failed" ? (
            <button onClick={resetToIdle} className="btn-primary">
              Publish another
            </button>
          ) : null
        }
      />

      {!effectiveHasMh && view === "idle" && (
        <div className="card border-amber-500/25 bg-amber-500/5 text-amber-200 p-4 text-sm mb-4">
          {publishAs ? (
            <>
              {publishAs.full_name || publishAs.email} hasn't saved valid
              MyHome credentials (with a phone number) yet. Pick another
              agent, or have them add credentials, before publishing.
            </>
          ) : (
            <>
              You haven't saved MyHome credentials yet.{" "}
              <a href="/profile" className="underline font-medium">
                Add them on your Profile
              </a>{" "}
              before publishing.
            </>
          )}
        </div>
      )}

      {view === "idle" && (
        <form id="publish-form" onSubmit={submit} className="space-y-4">
          {/* Publish-as picker — manager+ only, and only when there's at
              least one manageable teammate to assign the listing to. */}
          {canPublishAsOthers && publishableAgents.length > 0 && (
            <div className="card">
              <div className="p-4 flex flex-wrap items-center gap-3">
                <label htmlFor="publish-as" className="label !mb-0">
                  Publish as
                </label>
                <select
                  id="publish-as"
                  value={publishAsId}
                  onChange={(e) => setPublishAsId(e.target.value)}
                  disabled={running}
                  className="input !w-auto min-w-[240px] text-sm"
                >
                  <option value="">
                    Myself ({user?.full_name || user?.email})
                  </option>
                  {publishableAgents.map((m) => {
                    const credsOk =
                      m.has_myhome_creds && m.has_myhome_phone;
                    return (
                      <option key={m.id} value={m.id}>
                        {m.full_name || m.email}
                        {credsOk ? "" : " — no MyHome creds"}
                      </option>
                    );
                  })}
                </select>
                <span className="text-[11px] text-slate-500">
                  {publishAs
                    ? `Published under ${publishAs.full_name || publishAs.email}'s account, with their portal credentials.`
                    : "Published under your own account."}
                </span>
              </div>
            </div>
          )}

          {/* Source + description — one unified card */}
          <div className="card">
            <div className="p-5 space-y-5">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label">Source URL</label>
                  <span className="text-[11px] text-slate-600">myhome.ge · home.ss.ge</span>
                </div>
                <input
                  type="url"
                  required
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  disabled={running}
                  placeholder="https://www.myhome.ge/pr/…"
                  className="input mono text-[13px]"
                  autoFocus
                />
                {error && (
                  <div className="mt-2 text-[12px] text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
                    {error}
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label">Description</label>
                  <span className="text-[11px] text-slate-600 tabular-nums">
                    {description.length} chars
                  </span>
                </div>
                <textarea
                  rows={8}
                  required
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={running}
                  placeholder="Write the listing text (Georgian, Russian, or English)…"
                  className="input leading-relaxed"
                />
                {geminiConfigured && (
                  <div className="mt-2 flex items-center flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={geminiBusy || running || !url.trim()}
                      onClick={generateFromUrl}
                      className="btn-primary text-xs py-1.5 px-3"
                      title="Fetch this listing's details and write a fresh Georgian description"
                    >
                      {geminiBusy ? "Generating…" : "✨ Generate description"}
                    </button>
                    <button
                      type="button"
                      disabled={geminiBusy || running || !description.trim()}
                      onClick={() => polish("fix")}
                      className="btn-secondary text-xs py-1.5 px-3"
                    >
                      {geminiBusy ? "Polishing…" : "Polish current"}
                    </button>
                    <button
                      type="button"
                      disabled={geminiBusy || running}
                      onClick={() => setAiToolsOpen((v) => !v)}
                      className="text-xs text-slate-500 hover:text-slate-300 px-2 py-1.5"
                    >
                      {aiToolsOpen ? "Hide" : "Custom edit"} →
                    </button>
                    {aiToolsOpen && (
                      <div className="w-full flex gap-2">
                        <input
                          value={geminiPrompt}
                          onChange={(e) => setGeminiPrompt(e.target.value)}
                          disabled={geminiBusy || running}
                          placeholder='e.g. "shorten to 2 paragraphs" or "translate to English"'
                          className="input flex-1 text-xs"
                        />
                        <button
                          type="button"
                          disabled={geminiBusy || running || !geminiPrompt.trim()}
                          onClick={() => polish("custom")}
                          className="btn-secondary text-xs"
                        >
                          Apply
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Options card. Chips wrap into rows on phones; the conditional
              custom-price input + owner-phone field collapse to full-width
              underneath so neither has to truncate. */}
          <div className="card">
            <div className="p-4 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Chip
                  checked={mhPayBalance}
                  onChange={setMhPayBalance}
                  disabled={running}
                  title="When enabled, AgencyGrid pays the MyHome listing fee from your wallet balance."
                >
                  Auto-pay MyHome
                </Chip>
                <Chip
                  checked={useSs}
                  onChange={setUseSs}
                  disabled={running || !effectiveHasSs}
                  title={
                    effectiveHasSs
                      ? "Also publish the listing to SS.ge in parallel."
                      : publishAs
                        ? `${publishAs.full_name || publishAs.email} has no SS.ge credentials saved.`
                        : "Add SS.ge credentials in the vault to enable this."
                  }
                >
                  Also publish to SS.ge
                </Chip>
                <Chip
                  checked={customPriceEnabled}
                  onChange={setCustomPriceEnabled}
                  disabled={running}
                  title="Override the scraped price with a custom USD amount."
                >
                  Custom price
                </Chip>
              </div>

              {customPriceEnabled && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 flex-shrink-0">USD</span>
                  <div className="relative flex-1 sm:flex-none">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                      $
                    </span>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={customPriceUsd}
                      onChange={(e) => setCustomPriceUsd(e.target.value)}
                      disabled={running}
                      placeholder="85000"
                      className="input pl-6 pr-3 py-1.5 text-sm w-full sm:w-40 mono"
                    />
                  </div>
                </div>
              )}

              <div
                title="Optional. Saved on the placement so you can contact the owner later. MyHome masks scraped phones and SS.ge hides them entirely."
              >
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </span>
                  <input
                    type="tel"
                    inputMode="tel"
                    value={ownerPhone}
                    onChange={(e) => setOwnerPhone(e.target.value)}
                    disabled={running}
                    placeholder="Owner phone (optional)"
                    className="input pl-9 pr-3 py-2 text-sm w-full sm:max-w-xs mono"
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      )}

      {view === "running" && (
        <>
          <div className="card p-4 mb-4">
            <div className="flex items-center gap-3">
              <span className="relative flex-shrink-0">
                <span className="block w-2.5 h-2.5 rounded-full bg-accent-500" />
                <span className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-accent-500 animate-ping" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">
                  Source
                </div>
                <div className="text-sm mono text-slate-300 truncate">{url}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">
                  Progress
                </div>
                <div className="text-sm text-slate-200 tabular-nums">
                  {progressDone} / {progressTotal}
                </div>
              </div>
            </div>
            <div className="h-1 bg-white/[0.04] rounded-full overflow-hidden mt-3">
              <div
                className="h-full bg-accent-500 transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="card p-4 lg:col-span-2">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-3">
                Pipeline
              </div>
              <div className="space-y-0">
                {steps.map((s, i) => (
                  <div
                    key={s.id}
                    className={`flex items-center gap-3 py-2.5 ${
                      i < steps.length - 1 ? "border-b border-white/5" : ""
                    }`}
                  >
                    <StepIcon state={s.state} />
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-sm font-medium ${
                          s.state === "pending"
                            ? "text-slate-600"
                            : s.state === "active"
                              ? "text-accent-400"
                              : s.state === "done"
                                ? "text-slate-200"
                                : "text-rose-400"
                        }`}
                      >
                        {s.label}
                      </div>
                      {s.detail && (
                        <div className="text-[11px] text-slate-600 truncate mt-0.5">
                          {s.detail}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card lg:col-span-3 overflow-hidden">
              <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">
                  Live output
                </div>
                <div className="text-[10px] text-slate-600 mono tabular-nums">
                  {logs.length} lines
                </div>
              </div>
              <div
                ref={logPanelRef}
                className="p-3 h-[260px] sm:h-[320px] lg:h-[360px] overflow-y-auto bg-ink-950/60 text-[11.5px] mono leading-relaxed"
              >
                {logs.length === 0 ? (
                  <div className="text-slate-600 text-xs">waiting for events…</div>
                ) : (
                  logs.map((l, i) => (
                    <div
                      key={i}
                      className={
                        l.includes("[+]")
                          ? "text-emerald-400"
                          : l.includes("[!]") || l.includes("FAILED") || l.includes("error")
                            ? "text-rose-400"
                            : l.includes("[*]")
                              ? "text-accent-400"
                              : l.includes("[~]")
                                ? "text-amber-400"
                                : "text-slate-500"
                      }
                    >
                      {l}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {view === "done" && result && (
        <div className="space-y-4">
          <div className="card border-emerald-500/30 bg-emerald-500/[0.04] p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-5 h-5 text-emerald-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <div className="text-base font-semibold text-emerald-100">
                  Published successfully
                </div>
                <div className="text-[12px] text-slate-500 mt-0.5 mono truncate max-w-[600px]">
                  {url}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
              <Stat
                label={<PlatformLogo platform="myhome" size="xs" />}
                value={result.new_listing_id || "—"}
                mono
              />
              <Stat
                label={<PlatformLogo platform="ss" size="xs" />}
                value={result.ss_result?.application_id || "—"}
                mono
              />
              <Stat
                label="Images"
                value={`${result.images_uploaded} / ${result.images_scraped}`}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {result.new_listing_id && (
                <a
                  href={`https://www.myhome.ge/pr/${result.new_listing_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary justify-center"
                >
                  View on MyHome.ge ↗
                </a>
              )}
              {result.ss_result?.published && result.ss_result?.application_id && (
                <a
                  href={`https://home.ss.ge/ka/udzravi-qoneba/${result.ss_result.application_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary justify-center"
                >
                  View on SS.ge ↗
                </a>
              )}
            </div>

            {/* Partial failure — MyHome above is live, SS.ge errored. The
                full error is shown, plus a Retry that re-runs ONLY SS.ge
                so the MyHome listing is never re-created or re-charged. */}
            {result.ss_error && (
              <div className="mt-3 rounded-lg bg-rose-500/[0.07] border border-rose-500/25 p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <PlatformLogo platform="ss" size="xs" />
                  <span className="text-[12px] font-semibold text-rose-200">
                    SS.ge publish failed
                  </span>
                </div>
                <div className="text-[12px] text-rose-200/90 leading-relaxed whitespace-pre-wrap break-words rounded bg-black/20 border border-rose-500/15 p-2">
                  {result.ss_error}
                </div>
                {retrySsStarted ? (
                  <div className="mt-2 text-[12px] text-emerald-200 bg-emerald-500/10 border border-emerald-500/20 rounded px-2.5 py-1.5">
                    ↻ SS.ge retry started — track its progress in the publish
                    panel (bottom-right). Your MyHome listing is untouched.
                  </div>
                ) : (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={retrySs}
                      disabled={retrySsBusy || !jobRunId}
                      className="inline-flex items-center gap-1.5 rounded-md border border-accent-400/40 bg-accent-500/15 hover:bg-accent-500/25 text-accent-100 text-[12px] font-medium px-3 py-1.5 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Re-publish only SS.ge — your MyHome listing won't be re-created or re-charged"
                    >
                      {retrySsBusy ? "Starting…" : "↻ Retry SS.ge only"}
                    </button>
                    <span className="text-[11px] text-slate-500">
                      MyHome listing stays as-is.
                    </span>
                  </div>
                )}
                {retrySsError && (
                  <div className="mt-2 text-[11px] text-amber-200">
                    {retrySsError}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {view === "failed" && (
        <div className="card border-rose-500/25 bg-rose-500/[0.04] p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-rose-500/15 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-5 h-5 text-rose-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-base font-semibold text-rose-100">Pipeline failed</div>
              <div className="text-[12px] text-slate-500 mt-0.5 mono truncate">{url}</div>
            </div>
          </div>
          <div className="rounded-lg bg-black/30 border border-white/5 p-3 mono text-[12px] text-rose-200 break-words whitespace-pre-wrap">
            {fatal}
          </div>
          {logs.length > 0 && (
            <details className="mt-3">
              <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-300">
                Full log ({logs.length} lines)
              </summary>
              <div className="mt-2 rounded-lg bg-ink-950/60 p-3 max-h-56 overflow-y-auto text-[11px] mono leading-relaxed">
                {logs.map((l, i) => (
                  <div
                    key={i}
                    className={
                      l.includes("[+]")
                        ? "text-emerald-400"
                        : l.includes("[!]") || l.includes("FAILED") || l.includes("error")
                          ? "text-rose-400"
                          : "text-slate-500"
                    }
                  >
                    {l}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function Chip({
  checked,
  onChange,
  disabled,
  title,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      disabled={disabled}
      title={title}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
        disabled
          ? "opacity-40 cursor-not-allowed border-white/5 text-slate-600"
          : checked
            ? "border-accent-500/40 bg-accent-500/15 text-accent-300"
            : "border-white/10 bg-white/[0.02] text-slate-400 hover:text-slate-200 hover:border-white/20"
      }`}
    >
      <span
        className={`w-3.5 h-3.5 rounded-[4px] border flex items-center justify-center ${
          checked ? "bg-accent-500 border-accent-500" : "border-white/20"
        }`}
      >
        {checked && (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" className="w-2.5 h-2.5 text-white">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </span>
      {children}
    </button>
  );
}

function Stat({
  label,
  value,
  mono,
}: {
  label: React.ReactNode;
  value: string | number;
  mono?: boolean;
}) {
  return (
    <div className="bg-black/30 rounded-lg px-3 py-2.5 border border-white/5">
      <div className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold mb-1 flex items-center gap-1">
        {label}
      </div>
      <div className={`text-sm font-semibold text-slate-100 ${mono ? "mono" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function StepIcon({ state }: { state: StepState }) {
  if (state === "pending")
    return <span className="w-2 h-2 rounded-full bg-ink-700 ring-1 ring-white/10" />;
  if (state === "active")
    return (
      <span className="relative flex-shrink-0">
        <span className="block w-2.5 h-2.5 rounded-full bg-accent-500" />
        <span className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-accent-500 animate-ping" />
      </span>
    );
  if (state === "done")
    return (
      <span className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
        <svg
          className="w-3 h-3 text-emerald-400"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </span>
    );
  return (
    <span className="w-5 h-5 rounded-full bg-rose-500/15 flex items-center justify-center flex-shrink-0">
      <svg
        className="w-3 h-3 text-rose-400"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </span>
  );
}
